import { useEffect, useRef, useState } from 'react';
import type {
  ConsentSource,
  HearAboutUsChoice,
  InvestingExperienceChoice,
  PrimaryGoalChoice,
  SegmentationSelection,
  UserProfile,
  UserProfileUpdateInput,
} from '@covered-calls/shared';
import { recordConsent, updateProfile } from '../../services/profile-service';
import { getPrivacyUrl } from '../../services/runtime-config';


/** Consent decisions captured here originate from the onboarding flow (Decision 4). */
const CONSENT_SOURCE: ConsentSource = 'ONBOARDING';

/** Max lengths for the free-text controls (Req 2.1, 2.4). */
const DISPLAY_NAME_MAX = 100;
const OTHER_VALUE_MAX = 200;

interface OnboardingPromptProps {
  /** Current profile (may be a NOT_STARTED default with empty fields). */
  initialProfile: UserProfile;
  /** Called after the flow resolves so the gate can hide the prompt. */
  onComplete: () => void;
  /** Called when the user skips for this session only (state unchanged). */
  onSkip: () => void;
}

/** A single option entry for a fixed-choice <select>. */
interface ChoiceOption<C extends string> {
  value: C;
  label: string;
}

/**
 * A reasonable fixed list of countries/regions offered in the country select
 * (Req 2.2). Values are ISO-ish region codes, labels are human names. `OTHER`
 * is a catch-all so the list need not be exhaustive.
 */
const COUNTRIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'AU', label: 'Australia' },
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'IE', label: 'Ireland' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'JP', label: 'Japan' },
  { value: 'IN', label: 'India' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'OTHER', label: 'Other' },
];

const HEAR_ABOUT_US_OPTIONS: ReadonlyArray<ChoiceOption<HearAboutUsChoice>> = [
  { value: 'SEARCH', label: 'Search engine' },
  { value: 'SOCIAL_MEDIA', label: 'Social media' },
  { value: 'FRIEND', label: 'Friend or colleague' },
  { value: 'PODCAST', label: 'Podcast' },
  { value: 'OTHER', label: 'Other' },
];

const INVESTING_EXPERIENCE_OPTIONS: ReadonlyArray<
  ChoiceOption<InvestingExperienceChoice>
> = [
  { value: 'BEGINNER', label: 'Beginner' },
  { value: 'INTERMEDIATE', label: 'Intermediate' },
  { value: 'ADVANCED', label: 'Advanced' },
];

const PRIMARY_GOAL_OPTIONS: ReadonlyArray<ChoiceOption<PrimaryGoalChoice>> = [
  { value: 'INCOME', label: 'Income' },
  { value: 'GROWTH', label: 'Growth' },
  { value: 'HEDGING', label: 'Hedging' },
  { value: 'LEARNING', label: 'Learning' },
  { value: 'OTHER', label: 'Other' },
];

/** The empty sentinel value that keeps a select at "— Select —" (field unset). */
const UNSET = '';

/**
 * Build a `SegmentationSelection` from a raw select value and its optional
 * free-text. Returns `undefined` when the field is left unset, so the caller
 * can omit it from the update input entirely (Req 2.5). An `OTHER` choice
 * carries the trimmed `otherValue`; a blank free-text is dropped.
 */
function buildSelection<C extends string>(
  choice: string,
  otherValue: string
): SegmentationSelection<C> | undefined {
  if (choice === UNSET) {
    return undefined;
  }
  if (choice === 'OTHER') {
    const trimmed = otherValue.trim();
    return { choice: choice as C, otherValue: trimmed.length > 0 ? trimmed : undefined };
  }
  return { choice: choice as C };
}

/**
 * Common inline styles for form controls, using theme tokens only (Req 8.4;
 * `var(--color-*)` tokens only — no literal colours).
 */
const controlStyle = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid var(--color-input-border)',
  borderRadius: '4px',
  backgroundColor: 'var(--color-input-bg)',
  color: 'var(--color-text)',
  fontSize: '1rem',
} as const;

const labelStyle = {
  display: 'block',
  marginBottom: '0.25rem',
} as const;

const fieldStyle = {
  marginBottom: '1rem',
} as const;

/**
 * Post-sign-in onboarding modal (Component 8). Presents the optional profile
 * fields, three segmentation selects with conditional "Other" free-text, an
 * unticked marketing-consent checkbox grouped with a Privacy link, and the
 * Save / Skip for now / Don't ask again controls.
 *
 * On Save it issues `PUT /profile` with the non-empty fields then
 * `POST /profile/consent` (`source='ONBOARDING'`); on Don't ask again it issues
 * `PUT /profile` with `dismiss: true`, then records consent. The prompt is only
 * removed (via `onComplete`) after persistence succeeds; a failure surfaces a
 * `role="alert"` error and keeps the prompt (Req 1.7, 5.8). Skip for now is
 * session-only and performs no network call (Req 5.1, Decision 7).
 *
 * Validates: Requirements 1.3, 1.7, 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 3.1,
 * 3.3, 3.4, 3.5, 3.6, 3.7, 5.1, 5.5, 5.6, 5.8, 8.1, 8.2, 8.3, 8.4, 8.5, 8.7.
 */
export function OnboardingPrompt({
  initialProfile,
  onComplete,
  onSkip,
}: OnboardingPromptProps) {
  // Privacy information URL, resolved from runtime config (per-env
  // marketing/privacy URL) with a built-in fallback (Path 2 domain migration).
  const PRIVACY_URL = getPrivacyUrl();
  // Seed from the initial profile so a partially-complete profile is editable.
  const [displayName, setDisplayName] = useState(
    initialProfile.displayName ?? ''
  );
  const [countryRegion, setCountryRegion] = useState(
    initialProfile.countryRegion ?? UNSET
  );

  const [hearAboutUs, setHearAboutUs] = useState<string>(
    initialProfile.hearAboutUs?.choice ?? UNSET
  );
  const [hearAboutUsOther, setHearAboutUsOther] = useState(
    initialProfile.hearAboutUs?.otherValue ?? ''
  );

  const [investingExperience, setInvestingExperience] = useState<string>(
    initialProfile.investingExperience?.choice ?? UNSET
  );

  const [primaryGoal, setPrimaryGoal] = useState<string>(
    initialProfile.primaryGoal?.choice ?? UNSET
  );
  const [primaryGoalOther, setPrimaryGoalOther] = useState(
    initialProfile.primaryGoal?.otherValue ?? ''
  );

  // Marketing consent starts UNCHECKED on first display (Req 3.1, 3.7).
  const [consentChecked, setConsentChecked] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  // Place initial focus on the dialog heading when it mounts (Req 8.5).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  /**
   * Assemble a `UserProfileUpdateInput` from the non-empty fields only. Fields
   * left unset are omitted (undefined) so they are not overwritten (Req 2.5).
   */
  const buildInput = (dismiss: boolean): UserProfileUpdateInput => {
    const input: UserProfileUpdateInput = {};
    const trimmedName = displayName.trim();
    if (trimmedName.length > 0) {
      input.displayName = trimmedName;
    }
    if (countryRegion !== UNSET) {
      input.countryRegion = countryRegion;
    }
    const hear = buildSelection<HearAboutUsChoice>(hearAboutUs, hearAboutUsOther);
    if (hear) {
      input.hearAboutUs = hear;
    }
    const experience = buildSelection<InvestingExperienceChoice>(
      investingExperience,
      ''
    );
    if (experience) {
      input.investingExperience = experience;
    }
    const goal = buildSelection<PrimaryGoalChoice>(primaryGoal, primaryGoalOther);
    if (goal) {
      input.primaryGoal = goal;
    }
    if (dismiss) {
      input.dismiss = true;
    }
    return input;
  };

  /**
   * Persist the profile then record the current consent selection. Removes the
   * prompt only after BOTH succeed (Req 1.7); on any failure keeps the prompt
   * and surfaces the error, moving focus to it (Req 5.8, 8.7).
   */
  const persist = async (dismiss: boolean) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateProfile(buildInput(dismiss));
      // Consent is a dedicated action recorded with the current checkbox state
      // (Decision 4; Req 3.3, 3.4).
      await recordConsent(consentChecked, CONSENT_SOURCE);
      onComplete();
    } catch {
      setError("Couldn't save. Please try again.");
      // Move focus to the error region within the response (Req 8.7).
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setSubmitting(false);
    }
  };

  const showHearAboutUsOther = hearAboutUs === 'OTHER';
  const showPrimaryGoalOther = primaryGoal === 'OTHER';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        backgroundColor: 'var(--color-header-border)',
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-heading"
        style={{
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '1.5rem',
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
        }}
      >
        <h1
          ref={headingRef}
          className="focus-target"
          tabIndex={-1}
          id="onboarding-heading"
          style={{ marginBottom: '0.5rem' }}
        >
          Tell us about yourself
        </h1>

        <p style={{ marginBottom: '1.5rem', color: 'var(--color-muted)' }}>
          This is optional. Fill in as much or as little as you like, or skip it
          for now and complete it later in Settings.
        </p>

        {error && (
          <div
            ref={errorRef}
            role="alert"
            tabIndex={-1}
            className="focus-target"
            style={{ color: 'var(--color-danger)', marginBottom: '1rem' }}
          >
            {error}
          </div>
        )}

        {/* Display name (Req 2.1) */}
        <div style={fieldStyle}>
          <label htmlFor="ob-display-name" style={labelStyle}>
            Display name
          </label>
          <input
            id="ob-display-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={DISPLAY_NAME_MAX}
            style={controlStyle}
          />
        </div>

        {/* Country / region (Req 2.2) */}
        <div style={fieldStyle}>
          <label htmlFor="ob-country-region" style={labelStyle}>
            Country / region
          </label>
          <select
            id="ob-country-region"
            value={countryRegion}
            onChange={(e) => setCountryRegion(e.target.value)}
            style={controlStyle}
          >
            <option value={UNSET}>— Select —</option>
            {COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {/* How did you hear about us? (Req 2.3, 2.4) */}
        <div style={fieldStyle}>
          <label htmlFor="ob-hear-about-us" style={labelStyle}>
            How did you hear about us?
          </label>
          <select
            id="ob-hear-about-us"
            value={hearAboutUs}
            onChange={(e) => setHearAboutUs(e.target.value)}
            style={controlStyle}
          >
            <option value={UNSET}>— Select —</option>
            {HEAR_ABOUT_US_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {showHearAboutUsOther && (
            <div style={{ marginTop: '0.5rem' }}>
              <label htmlFor="ob-hear-about-us-other" style={labelStyle}>
                Tell us how
              </label>
              <input
                id="ob-hear-about-us-other"
                type="text"
                value={hearAboutUsOther}
                onChange={(e) => setHearAboutUsOther(e.target.value)}
                maxLength={OTHER_VALUE_MAX}
                style={controlStyle}
              />
            </div>
          )}
        </div>

        {/* Investing experience (Req 2.3 — no Other) */}
        <div style={fieldStyle}>
          <label htmlFor="ob-investing-experience" style={labelStyle}>
            Investing experience
          </label>
          <select
            id="ob-investing-experience"
            value={investingExperience}
            onChange={(e) => setInvestingExperience(e.target.value)}
            style={controlStyle}
          >
            <option value={UNSET}>— Select —</option>
            {INVESTING_EXPERIENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Primary goal (Req 2.3, 2.4) */}
        <div style={fieldStyle}>
          <label htmlFor="ob-primary-goal" style={labelStyle}>
            Primary goal
          </label>
          <select
            id="ob-primary-goal"
            value={primaryGoal}
            onChange={(e) => setPrimaryGoal(e.target.value)}
            style={controlStyle}
          >
            <option value={UNSET}>— Select —</option>
            {PRIMARY_GOAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {showPrimaryGoalOther && (
            <div style={{ marginTop: '0.5rem' }}>
              <label htmlFor="ob-primary-goal-other" style={labelStyle}>
                Tell us your goal
              </label>
              <input
                id="ob-primary-goal-other"
                type="text"
                value={primaryGoalOther}
                onChange={(e) => setPrimaryGoalOther(e.target.value)}
                maxLength={OTHER_VALUE_MAX}
                style={controlStyle}
              />
            </div>
          )}
        </div>

        {/* Marketing consent grouped with the privacy link (Req 3.1, 3.5-3.7) */}
        <fieldset
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
            padding: '0.75rem',
            marginBottom: '1.5rem',
          }}
        >
          <legend style={{ padding: '0 0.25rem', color: 'var(--color-muted)' }}>
            Marketing
          </legend>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <input
              id="ob-marketing-consent"
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              style={{ marginTop: '0.2rem' }}
            />
            <label htmlFor="ob-marketing-consent">
              I&apos;d like to receive product news and marketing emails.
            </label>
          </div>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
              Privacy information
            </a>
          </p>
        </fieldset>

        {/* Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={() => void persist(false)}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: 'var(--color-brand)',
              color: 'var(--color-on-brand)',
              border: 'none',
              borderRadius: '4px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Saving...' : 'Save'}
          </button>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              onClick={onSkip}
              disabled={submitting}
              style={{
                flex: 1,
                padding: '0.6rem',
                background: 'none',
                color: 'var(--color-link)',
                border: '1px solid var(--color-border)',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
              }}
            >
              Skip for now
            </button>
            <button
              type="button"
              onClick={() => void persist(true)}
              disabled={submitting}
              style={{
                flex: 1,
                padding: '0.6rem',
                background: 'none',
                color: 'var(--color-muted)',
                border: '1px solid var(--color-border)',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
              }}
            >
              Don&apos;t ask again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
